import {
  INITIAL_PAYMENT_LINK_VALUES,
  IPaymentLinkFormValues,
} from '../schema';
import {
  IPaymentState,
  IValidatePaymentLinkResponse,
} from '../types/types';
import { getDefaultPaymentMethod } from './paymentMethods';

type ApiPaymentLink = IValidatePaymentLinkResponse['response'];

export interface IMappedPaymentLink {
  isExpired: boolean;
  paymentState: IPaymentState;
  formValues: IPaymentLinkFormValues;
}

export const createEmptyPaymentState = (
  link: string | undefined,
): IPaymentState => ({
  link,
  allowedPaymentMethods: [],
  plan: undefined,
  addons: [],
  couponId: null,
  couponCode: null,
  subtotal: 0,
  discountAmount: 0,
  finalPrice: 0,
  paidAt: undefined,
  submittedAt: undefined,
});

export const mapPaymentLinkResponse = (
  link: string,
  paymentLink: ApiPaymentLink,
): IMappedPaymentLink => {
  const allowedPaymentMethods = paymentLink.allowed_payment_methods ?? [];

  const discounts = paymentLink.discounts ?? [];
  const discountFor = (targetId: string) =>
    discounts
      .filter(discount => discount.target_id === targetId)
      .reduce((total, discount) => total + discount.amount, 0);

  const addons = (paymentLink.addons ?? []).map(addon => {
    const total = addon.total ?? addon.price * (addon.quantity || 1);

    return {
      ...addon,
      total,
      discountedTotal: total - discountFor(addon.addon_id),
    };
  });

  const planId = paymentLink.plan_id ?? '';
  const planAmount = paymentLink.plan_value ?? 0;
  const subtotal =
    paymentLink.subtotal ??
    addons.reduce((total, addon) => total + addon.total, planAmount);
  const discountAmount = paymentLink.discount_amount ?? 0;

  const paymentState: IPaymentState = {
    link,
    allowedPaymentMethods,
    plan: {
      id: planId,
      name: paymentLink.plan_name,
      amount: planAmount,
      discountedAmount: planAmount - discountFor(planId),
    },
    addons,
    couponId: paymentLink.coupon_id,
    couponCode: paymentLink.coupon_code ?? null,
    subtotal,
    discountAmount,
    finalPrice: paymentLink.final_price ?? subtotal - discountAmount,
    paidAt: paymentLink.paid_at,
    submittedAt: paymentLink.submitted_at,
  };

  const defaultPaymentMethod = getDefaultPaymentMethod(allowedPaymentMethods);
  const customer = paymentLink.customer;

  const formValues: IPaymentLinkFormValues = {
    ...INITIAL_PAYMENT_LINK_VALUES,
    requiresCustomerData: Boolean(paymentLink.requires_customer_data),
    ...(defaultPaymentMethod && {
      paymentMethodSelected: defaultPaymentMethod,
    }),
    ...(customer && {
      name: customer.name ?? '',
      email: customer.email ?? '',
      document: customer.document ?? '',
      zipCode: customer.zipcode ?? '',
      state: customer.state ?? '',
      city: customer.city ?? '',
      address: customer.street ?? '',
      addressNumber: customer.street_number ?? '',
      neighborhood: customer.neighborhood ?? '',
      addressComplement: customer.complement ?? '',
    }),
  };

  return {
    isExpired: paymentLink.is_expired,
    paymentState,
    formValues,
  };
};
